import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Switch,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import axios from "axios";

/* ─── Inline SVG Icons ─── */
const ReplaceImageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#94a3b8" strokeWidth="1.6" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="#94a3b8" />
    <path d="M21 15l-5-5L5 21" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlayIcon = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="18" r="18" fill="rgba(0,0,0,0.5)" />
    <polygon points="14,11 26,18 14,25" fill="white" />
  </svg>
);

/* ─── Platform Preview Tab Panel ─── */
interface PreviewPanelProps {
  platform: string;
  caption: string;
  mediaType: "image" | "video";
  coverImageUrl?: string;
  videoThumbnailUrl?: string;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  platform,
  caption,
  mediaType,
  coverImageUrl,
  videoThumbnailUrl,
}) => {
  const platformColors: Record<string, string> = {
    LinkedIn: "#0a66c2",
    YouTube: "#ff0000",
    "X (Twitter)": "#000000",
    Facebook: "#1877f2",
  };

  const avatarInitials = "RH";

  return (
    <Box
      sx={{
        background: "#1a1f2e",
        borderRadius: "10px",
        p: 2,
        border: "1px solid #2a3040",
      }}
    >
      {/* Post Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {avatarInitials}
        </Box>
        <Box flex={1}>
          <Typography sx={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
            Rohan Hall
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 11 }}>Just now</Typography>
        </Box>
        <Box sx={{ color: platformColors[platform] || "#64748b", fontSize: 11, fontWeight: 600 }}>
          ···
        </Box>
      </Stack>

      {/* Caption Preview */}
      <Typography
        sx={{
          color: "#cbd5e1",
          fontSize: 12,
          lineHeight: 1.6,
          mb: 1.5,
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {caption || "Your caption will appear here."}
      </Typography>

      {/* Media Thumbnail */}
      <Box
        sx={{
          width: "100%",
          height: 140,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          borderRadius: "8px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          border: "1px solid #2a3040",
        }}
      >
        {mediaType === "image" && coverImageUrl ? (
          <Box
            component="img"
            src={coverImageUrl}
            alt="cover"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : mediaType === "video" && videoThumbnailUrl ? (
          <>
            <Box
              component="img"
              src={videoThumbnailUrl}
              alt="video thumbnail"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box sx={{ position: "absolute", right: 16, bottom: 10, zIndex: 2 }}>
              <PlayIcon />
            </Box>
          </>
        ) : (
          <>
            {/* Fallback placeholder when no media URL */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "55%",
                background: "linear-gradient(135deg, #1e3a5f 0%, #0a1628 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="60" height="70" viewBox="0 0 60 70" fill="none" opacity="0.6">
                <ellipse cx="30" cy="28" rx="18" ry="20" stroke="#4a90d9" strokeWidth="1.5" fill="none" />
                <rect x="15" y="48" width="30" height="16" rx="3" stroke="#4a90d9" strokeWidth="1.5" fill="none" />
                <circle cx="22" cy="26" r="4" fill="#4a90d9" opacity="0.8" />
                <circle cx="38" cy="26" r="4" fill="#4a90d9" opacity="0.8" />
                <path d="M22 36 Q30 40 38 36" stroke="#4a90d9" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Box>
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: "50%",
                background: "linear-gradient(135deg, #1a1f2e 0%, #2d1b4e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="50" height="65" viewBox="0 0 50 65" fill="none" opacity="0.7">
                <circle cx="25" cy="14" r="10" fill="#4a5568" />
                <path d="M5 55 Q5 35 25 35 Q45 35 45 55" fill="#4a5568" />
              </svg>
            </Box>
            {mediaType === "video" && (
              <Box sx={{ position: "absolute", right: 16, bottom: 10, zIndex: 2 }}>
                <PlayIcon />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

/* ─── Main Component ─── */
const EditClip: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Read clip data passed from the listing page via route state ──
  const { clipId, clipData } = (location.state as any) || {};

  const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
  const user_email = localStorage.getItem("user_email") || "";

  // ── Form state — initialised from route state, falls back to empty ──
  const [caption, setCaption] = useState<string>(clipData?.caption || "");
  const [callToAction, setCallToAction] = useState<string>(clipData?.call_to_action || "");
  const [link, setLink] = useState<string>(clipData?.link || "");
  const [utmEnabled, setUtmEnabled] = useState<boolean>(clipData?.utm_enabled ?? false);
  const [coverImageUrl, setCoverImageUrl] = useState<string>(clipData?.cover_image_url || "");
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState<string>(clipData?.video_thumbnail_url || "");

  const [autoSave] = useState(true);
  const [previewTab, setPreviewTab] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const platforms = ["LinkedIn", "YouTube", "X (Twitter)", "Facebook"];

  // ── If only clipId is passed (no full clipData), fetch from API ──
  useEffect(() => {
    if (!clipId || clipData) return;

    const fetchClip = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${apiUrl}/api/prompts/clips/${clipId}?user_email=${user_email}`
        );
        const data = res.data;
        setCaption(data.caption || "");
        setCallToAction(data.call_to_action || "");
        setLink(data.link || "");
        setUtmEnabled(data.utm_enabled ?? false);
        setCoverImageUrl(data.cover_image_url || "");
        setVideoThumbnailUrl(data.video_thumbnail_url || "");
      } catch (error) {
        console.error("Error fetching clip data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClip();
  }, [clipId]);

  // ── Save updated clip data to API ──
  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await axios.put(`${apiUrl}/api/prompts/clips/${clipId}`, {
        user_email,
        caption,
        call_to_action: callToAction,
        link,
        utm_enabled: utmEnabled,
      });
    } catch (error) {
      console.error("Error updating clip:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#0d1117",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "#6366f1" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#0d1117",
        color: "#e2e8f0",
        fontFamily: "'Inter', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ─── Top Bar ─── */}
      <Box
        sx={{
          borderBottom: "1px solid #1e2535",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#0d1117",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          sx={{
            color: "#7c8db0",
            fontSize: 13,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": { color: "#e2e8f0", background: "transparent" },
            p: 0,
          }}
        >
          Back to Dashboard
        </Button>

        <Box textAlign="center">
          <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: 18 }}>
            Edit Clip
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>
            Refine your content and make it perfect before publishing.
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          {autoSave && (
            <Stack direction="row" alignItems="center" spacing={0.7}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#22c55e",
                  boxShadow: "0 0 6px #22c55e",
                }}
              />
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>Auto-save</Typography>
            </Stack>
          )}
          <Typography sx={{ color: "#475569", fontSize: 12 }}>Saved just now</Typography>
        </Stack>
      </Box>

      {/* ─── Body ─── */}
      <Box sx={{ flex: 1, display: "flex", gap: 0, overflow: "hidden" }}>

        {/* ─── LEFT PANEL: Edit Form ─── */}
        <Box
          sx={{
            flex: "0 0 55%",
            borderRight: "1px solid #1e2535",
            overflowY: "auto",
            p: 3,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#2a3040", borderRadius: 2 },
          }}
        >
          {/* Content Tab */}
          <Box sx={{ borderBottom: "1px solid #1e2535", mb: 3 }}>
            <Tabs
              value={0}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: "none",
                  minHeight: 36,
                  px: 2,
                  py: 0,
                },
                "& .Mui-selected": { color: "#e2e8f0" },
                "& .MuiTabs-indicator": { background: "#6366f1", height: 2 },
              }}
            >
              <Tab label="Content" />
            </Tabs>
          </Box>

          {/* Post Caption */}
          <Box mb={3}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 600, mb: 1 }}>
              Post Caption
            </Typography>
            <Box sx={{ position: "relative" }}>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={8}
                style={{
                  width: "100%",
                  background: "#111827",
                  border: "1px solid #1e2535",
                  borderRadius: 8,
                  color: "#cbd5e1",
                  fontSize: 13,
                  lineHeight: 1.7,
                  padding: "12px 14px",
                  resize: "vertical",
                  fontFamily: "'Inter', sans-serif",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#6366f1"; }}
                onBlur={(e) => { e.target.style.borderColor = "#1e2535"; }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 0.5 }}>
                <Typography sx={{ color: "#475569", fontSize: 11 }}>
                  {caption.length}/600
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Media */}
          <Box mb={3}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 600, mb: 1.5 }}>
              Media
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {/* Cover Image */}
              <Box
                sx={{
                  width: 140,
                  height: 100,
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #1e293b 0%, #0a1628 100%)",
                  border: "1px solid #2a3040",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  flexShrink: 0,
                  cursor: "pointer",
                  "&:hover .replace-overlay": { opacity: 1 },
                }}
              >
                {coverImageUrl ? (
                  <Box
                    component="img"
                    src={coverImageUrl}
                    alt="cover"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <svg width="40" height="48" viewBox="0 0 60 70" fill="none" opacity="0.5">
                    <ellipse cx="30" cy="28" rx="18" ry="20" stroke="#4a90d9" strokeWidth="1.5" fill="none" />
                    <rect x="15" y="48" width="30" height="16" rx="3" stroke="#4a90d9" strokeWidth="1.5" fill="none" />
                    <circle cx="22" cy="26" r="4" fill="#4a90d9" opacity="0.8" />
                    <circle cx="38" cy="26" r="4" fill="#4a90d9" opacity="0.8" />
                  </svg>
                )}
                <Box
                  className="replace-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0,0,0,0.7)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.2s",
                    gap: 0.5,
                  }}
                >
                  <ReplaceImageIcon />
                  <Typography sx={{ color: "#94a3b8", fontSize: 10 }}>Replace Image</Typography>
                </Box>
              </Box>

              {/* Video Thumbnail */}
              <Box
                sx={{
                  flex: 1,
                  height: 100,
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #1a1f2e 0%, #2d1b4e 100%)",
                  border: "1px solid #2a3040",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {videoThumbnailUrl ? (
                  <Box
                    component="img"
                    src={videoThumbnailUrl}
                    alt="video thumbnail"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <svg width="45" height="55" viewBox="0 0 50 65" fill="none" opacity="0.5">
                    <circle cx="25" cy="14" r="10" fill="#4a5568" />
                    <path d="M5 55 Q5 35 25 35 Q45 35 45 55" fill="#4a5568" />
                  </svg>
                )}
                <Box sx={{ position: "absolute", right: 10, bottom: 8 }}>
                  <svg width="26" height="26" viewBox="0 0 36 36" fill="none">
                    <circle cx="18" cy="18" r="18" fill="rgba(0,0,0,0.55)" />
                    <polygon points="14,11 26,18 14,25" fill="white" />
                  </svg>
                </Box>
              </Box>
            </Stack>
            <Typography sx={{ color: "#475569", fontSize: 11, mt: 1 }}>
              Recommended size: 1080x350 (Portrait)
            </Typography>
          </Box>

          {/* Call to Action */}
          <Box mb={3}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 600, mb: 1 }}>
              Call to Action{" "}
              <Typography component="span" sx={{ color: "#475569", fontSize: 12, fontWeight: 400 }}>
                (Optional)
              </Typography>
            </Typography>
            <Box sx={{ position: "relative" }}>
              <input
                value={callToAction}
                onChange={(e) => setCallToAction(e.target.value)}
                placeholder="Share your thoughts below! 🔥"
                style={{
                  width: "100%",
                  background: "#111827",
                  border: "1px solid #1e2535",
                  borderRadius: 8,
                  color: "#cbd5e1",
                  fontSize: 13,
                  padding: "10px 60px 10px 14px",
                  fontFamily: "'Inter', sans-serif",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#6366f1"; }}
                onBlur={(e) => { e.target.style.borderColor = "#1e2535"; }}
              />
              <Typography
                sx={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#475569",
                  fontSize: 11,
                  pointerEvents: "none",
                }}
              >
                {callToAction.length}/100
              </Typography>
            </Box>
          </Box>

          {/* Link */}
          <Box mb={4}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 600, mb: 1 }}>
              Link{" "}
              <Typography component="span" sx={{ color: "#475569", fontSize: 12, fontWeight: 400 }}>
                (Optional)
              </Typography>
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://www.ocesha.ai/ai-insights"
                style={{
                  flex: 1,
                  background: "#111827",
                  border: "1px solid #1e2535",
                  borderRadius: 8,
                  color: "#cbd5e1",
                  fontSize: 13,
                  padding: "10px 14px",
                  fontFamily: "'Inter', sans-serif",
                  outline: "none",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#6366f1"; }}
                onBlur={(e) => { e.target.style.borderColor = "#1e2535"; }}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={utmEnabled}
                  onChange={(e) => setUtmEnabled(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366f1" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6366f1" },
                  }}
                />
                <Typography sx={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>
                  Add UTM parameters
                </Typography>
                <Tooltip title="Track campaign traffic with UTM parameters" arrow>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ cursor: "help" }}>
                    <circle cx="12" cy="12" r="9" stroke="#475569" strokeWidth="1.8" />
                    <circle cx="12" cy="8" r="0.8" fill="#475569" />
                    <path d="M12 11v5" stroke="#475569" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {/* Update Post Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleUpdate}
            disabled={isUpdating}
            startIcon={
              isUpdating
                ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                : <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              textTransform: "none",
              borderRadius: 2,
              py: 1.4,
              boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #5254cc 0%, #6d28d9 100%)",
                boxShadow: "0 6px 20px rgba(99,102,241,0.5)",
              },
              "&.Mui-disabled": {
                background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)",
                opacity: 0.7,
                color: "#fff",
              },
            }}
          >
            {isUpdating ? "Updating..." : "Update Post"}
          </Button>
        </Box>

        {/* ─── RIGHT PANEL: Preview ─── */}
        <Box
          sx={{
            flex: "0 0 45%",
            overflowY: "auto",
            p: 3,
            background: "#0a0e17",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#2a3040", borderRadius: 2 },
          }}
        >
          <Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            Preview
          </Typography>
          <Typography sx={{ color: "#475569", fontSize: 12, mb: 2 }}>
            See how your post will look on different platforms.
          </Typography>

          {/* Platform Tabs */}
          <Tabs
            value={previewTab}
            onChange={(_, v) => setPreviewTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              mb: 2.5,
              minHeight: 34,
              background: "#111827",
              borderRadius: 2,
              p: 0.5,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                color: "#64748b",
                fontSize: 12,
                fontWeight: 500,
                textTransform: "none",
                minHeight: 28,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                transition: "all 0.2s",
              },
              "& .Mui-selected": {
                color: "#e2e8f0",
                background: "#1e2535",
              },
            }}
          >
            {platforms.map((p) => (
              <Tab
                key={p}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    {p === "LinkedIn" && <LinkedInIcon sx={{ fontSize: 14, color: "#0a66c2" }} />}
                    {p === "YouTube" && <YouTubeIcon sx={{ fontSize: 14, color: "#ff0000" }} />}
                    {p === "X (Twitter)" && (
                      <CloseIcon
                        sx={{
                          fontSize: 13,
                          color: previewTab === platforms.indexOf(p) ? "#e2e8f0" : "#64748b",
                        }}
                      />
                    )}
                    {p === "Facebook" && <FacebookIcon sx={{ fontSize: 14, color: "#1877f2" }} />}
                    <span>{p}</span>
                  </Stack>
                }
              />
            ))}
          </Tabs>

          {/* Preview Cards */}
          {platforms.map(
            (platform, idx) =>
              idx === previewTab && (
                <Stack key={platform} spacing={2}>
                  <PreviewPanel
                    platform={platform}
                    caption={caption}
                    mediaType="image"
                    coverImageUrl={coverImageUrl}
                    videoThumbnailUrl={videoThumbnailUrl}
                  />
                  <PreviewPanel
                    platform={platform}
                    caption={caption}
                    mediaType="video"
                    coverImageUrl={coverImageUrl}
                    videoThumbnailUrl={videoThumbnailUrl}
                  />
                </Stack>
              )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EditClip;